import { useContext } from "react";
import { useUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import {
  generateRenderWeekdayLabels,
  generateRenderDays,
  generateCalculateDateStyle,
  generatePriceLabel,
} from "@b2bportal/core-ui";
import {
  type MonthType,
  useMonth,
  type FirstDayOfWeek,
} from "@datepicker-react/hooks";
import { type DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import { DateRangePickerContext } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./Month.module.scss";

// Re-export helper functions from core-ui
export {
  generateRenderWeekdayLabels,
  generateRenderDays,
  generateCalculateDateStyle,
  generatePriceLabel,
};

export interface IMonthProps extends MonthType {
  firstDayOfWeek: FirstDayOfWeek;
  hideWeekdayLabels?: boolean;
  hideHeader?: boolean;
  hideBody?: boolean;
  headerClassName?: string;
  bodyClassName?: string;
  rootClassName?: string;
  monthFormat?: DateTimeFormatStyle;
  dayOfWeekFormat?: DateTimeFormatStyle;
  isMiniCalendar?: boolean;
  onClick?: () => void;
  className?: string;
  nextMonth?: (date: string) => void;
  prevMonth?: (date: string) => void;
  currentFocusDate?: Date;
  setCurrentFocusDate?: (date?: Date) => void;
  setIsDateFocused?: (bool: boolean) => void;
  isDateFocused?: boolean;
  priceTags?: string[];
}

export const Month = ({
  year,
  month,
  firstDayOfWeek,
  hideWeekdayLabels,
  hideHeader,
  hideBody,
  headerClassName,
  bodyClassName,
  rootClassName,
  monthFormat,
  dayOfWeekFormat,
  isMiniCalendar,
  onClick,
  className,
  currentFocusDate,
  setCurrentFocusDate,
  setIsDateFocused,
  isDateFocused,
  priceTags,
}: IMonthProps) => {
  const styles = useUiStyles(CoreUiComponents.DatePickerMonth, defaultStyles);

  const [block, cn] = useModuleBEM(styles, CoreUiComponents.DatePickerMonth);

  const { formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const weekdayLabelFormat = (date: Date) =>
    formatDateTime(
      date,
      dayOfWeekFormat ?? DateTimeFormatStyle.Custom({ weekday: "short" })
    );
  const dayLabelFormat = (date: Date) =>
    formatDateTime(date, DateTimeFormatStyle.Custom({ day: "numeric" }));

  // PERKSPOT OVERRIDE: Always show full month name and year
  const monthLabelFormat = (date: Date) => {
    const style = DateTimeFormatStyle.Custom({
      month: "long",
      year: "numeric",
    });
    return formatDateTime(date, style);
  };

  const { months: monthBuckets } = useContext(DateRangePickerContext);

  const { days, weekdayLabels, monthLabel } = useMonth({
    year,
    month,
    firstDayOfWeek,
    weekdayLabelFormat,
    monthLabelFormat,
    dayLabelFormat,
  });

  const handleClick = () => onClick && onClick();
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={clsx(block, rootClassName, className, {
        [cn("mini-calendar")]: isMiniCalendar,
      })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {!hideHeader && (
        <div className={clsx(cn("header"), headerClassName)}>
          <div className={cn("header-contents")}>
            <div className={cn("label")}>{monthLabel}</div>
          </div>
        </div>
      )}
      <div className={clsx(cn("body"), bodyClassName)} aria-label={monthLabel}>
        {!hideWeekdayLabels &&
          generateRenderWeekdayLabels({ month, year })(weekdayLabels)}
        {!hideBody &&
          generateRenderDays({
            year,
            monthLabel,
            month: month,
            months: monthBuckets,
            isMiniDay: isMiniCalendar,
            className: className,
            currentFocusDate,
            setCurrentFocusDate,
            setIsDateFocused,
            isDateFocused,
            priceTags,
          })(days)}
      </div>
    </div>
  );
};
